<template>
    <div>
        <div class="pb-10 max-w-465">
            <h2 class="mb-30">Create a New Client</h2>

            <form-wrapper :validator="$v.client">

                <form-group name="name" label="Name" class="mb-14">
                    <input
                        @input="delayTouch($v.client.name)"
                        class="max-w-2/3"
                        slot-scope="{attributes}"
                        v-model="client.name"
                        v-bind="attributes"
                        type="text"
                        placeholder="Enter client name..."
                    >
                </form-group>

                <form-group name="currency" label="Currency" class="mb-14">
                    <multiselect
                        :options="currencyOptions"
                        :searchable="false"
                        :show-labels="false"
                        class="w-2/3"
                        placeholder="Select currency..."
                        v-model="client.currency"
                    ></multiselect>
                </form-group>

                <form-group name="data_started_at" label="Data start date" class="mb-14">
                    <date-picker
                        :popover="{visibility: 'click'}"
                        :theme="datePickerTheme"
                        class="w-2/3"
                        is-required
                        v-model="client.data_started_at"
                    />
                </form-group>

                <!-- <div class="md:flex md:items-center mb-14">
                    <label class="block pr-4 mb-1 md:text-left md:mb-0">Users</label>
                    <multiselect
                        :close-on-select="false"
                        :custom-label="userLabel"
                        :multiple="true"
                        :options="users"
                        :searchable="true"
                        :show-labels="false"
                        class="md:ml-auto md:w-2/3"
                        label="full_name"
                        placeholder="Start typing..."
                        track-by="id"
                        v-model="client.users"
                    ></multiselect>
                </div> -->

                <div class="md:ml-auto md:w-2/3">
                    <button @click="onSelectImage" class="w-full mb-14">
                        <span v-if="imageSrc">Change image</span>
                        <span v-else>Select image</span>
                        <input ref="fileInput" @input="setImage" type="file" name="image" accept="image/*" class="hidden" />
                    </button>

                    <template v-if="imageSrc">
                        <vue-cropper
                            :auto-crop-area="1"
                            :guides="true"
                            :src="imageSrc"
                            class="w-full mr-2 mb-14"
                            ref="cropper"
                        ></vue-cropper>

                        <button @click="cropImage" class="w-full mb-14">Crop image</button>

                        <!-- Cropped image previewer -->
                        <img :src="imageCroppedSrc" class="w-full mb-14"/>
                    </template>

                </div>

            </form-wrapper>

            <div v-if="errors" class="flex flex-col px-6 pt-6 pb-4 my-6 bg-red-700">
                <span v-for="error in errors" class="mb-2 text-white">
                    {{error[0]}}
                </span>
            </div>
        </div>

        <div class="flex justify-end md:max-w-465">
            <button @click="onCreate" :disabled="!canCreate" class="ml-4">
                <span v-if="creating">Creating...</span>
                <span v-else>Create client</span>
            </button>
        </div>
    </div>
</template>

<script>
    import { between, integer, maxLength, minLength, required } from "vuelidate/lib/validators"
    import { call, get, sync } from 'vuex-pathify'
    import DatePicker from 'v-calendar/lib/components/date-picker.umd'
    import { format } from 'date-fns'
    import imageUploadMixin from "../../mixins/imageUpload"
    import Multiselect from "vue-multiselect"
    import validationMixin from "../../mixins/validation"

    export default {
        mixins: [
            imageUploadMixin,
            validationMixin
        ],

        components: {
            DatePicker,
            Multiselect,
        },

        data() {
            return {
                datePickerTheme: {
                    color: 'pink',
                    isDark: true,
                    container: {
                        light:
                        'vc-text-gray-900 vc-bg-white vc-border vc-border-gray-400',
                        dark:
                        'vc-text-gray-200 bg-blue-darkest vc-border vc-border-gray-700',
                    },
                    header: {
                        light: 'vc-text-gray-900',
                        dark: 'vc-text-gray-200',
                    },
                    title: {
                        light: 'vc-text-lg vc-text-gray-800 vc-font-semibold hover:text-pink',
                        dark: 'vc-text-lg vc-text-gray-100 vc-font-semibold hover:text-pink',
                    },
                    arrows: {
                        light:
                        'vc-text-gray-600 vc-border-2 vc-border-transparent hover:text-pink hover:vc-bg-gray-300 focus:vc-border-gray-300',
                        dark:
                        'vc-text-white vc-border-2 vc-border-transparent hover:text-pink focus:vc-border-gray-700',
                    },
                    weekdays: {
                        light: 'vc-text-sm vc-font-bold vc-text-gray-500',
                        dark: 'vc-text-sm vc-font-bold text-pink',
                    },
                    navPopoverContainer: {
                        light:
                        'vc-text-sm vc-font-semibold vc-text-white vc-bg-gray-800 vc-border vc-border-gray-700 vc-p-1 vc-shadow',
                        dark:
                        'vc-text-sm vc-font-semibold vc-text-gray-800 vc-bg-white vc-border vc-border-gray-100 vc-p-1 vc-shadow',
                    },
                    navTitle: {
                        light:
                        'text-pink vc-font-bold vc-leading-snug vc-px-2 vc-py-1 vc-border-2 vc-border-transparent hover:vc-bg-gray-900 focus:border-pink',
                        dark:
                        'vc-text-gray-900 vc-font-bold vc-leading-snug vc-px-2 vc-py-1 vc-border-2 vc-border-transparent hover:vc-bg-gray-200 focus:border-pink',
                    },
                    navArrows: {
                        light:
                        'vc-leading-snug vc-border-2 vc-border-transparent hover:vc-bg-gray-900 focus:border-pink',
                        dark:
                        'vc-leading-snug vc-border-2 vc-border-transparent hover:vc-bg-gray-200 focus:border-pink',
                    },
                    navCell: {
                        light:
                        'vc-w-12 vc-font-semibold vc-cursor-pointer vc-text-center vc-leading-snug vc-py-1 vc-border-2 vc-border-transparent hover:vc-bg-gray-900 hover:vc-shadow-inner hover:vc-text-white focus:border-pink',
                        dark:
                        'vc-w-12 vc-font-semibold vc-cursor-pointer vc-text-center vc-leading-snug vc-py-1 vc-border-2 vc-border-transparent hover:vc-bg-gray-200 hover:vc-text-gray-900 focus:border-pink',
                    },
                    navCellInactive: 'vc-border-transparent',
                    navCellInactiveCurrent: {
                        light: 'text-pink vc-font-bold border-pink',
                        dark: 'text-pink vc-font-bold border-pink',
                    },
                    navCellActive: {
                        light:
                        'bg-pink text-pink vc-border-transparent vc-font-bold vc-shadow',
                        dark:
                        'bg-pink vc-text-white vc-border-transparent vc-font-bold vc-shadow',
                    },
                    dayNotInMonth: 'vc-opacity-0 vc-pointer-events-none',
                    dayContent:
                        'vc-font-medium vc-text-sm vc-cursor-pointer focus:vc-font-bold',
                    dayContentDisabled: {
                        light: 'vc-text-gray-400',
                        dark: 'vc-text-gray-600',
                    },
                    dayPopoverContainer: {
                        light:
                        'vc-text-xs vc-text-white vc-font-medium vc-bg-gray-800 vc-border vc-border-gray-700 vc-px-2 vc-py-1 vc-shadow',
                        dark:
                        'vc-text-xs vc-text-gray-900 vc-font-medium vc-bg-white vc-border vc-border-gray-200 vc-px-2 vc-py-1 vc-shadow',
                    },
                    dayPopoverHeader: {
                        light: 'vc-text-xs vc-text-gray-300 vc-font-semibold',
                        dark: 'vc-text-xs vc-text-gray-700 vc-font-semibold',
                    },
                    highlightBaseFillMode: 'light',
                    highlightStartEndFillMode: 'solid',
                    highlightStartEndClass: '',
                    bgLow: {
                        light: 'vc-bg-white vc-border-2 border-pink',
                        dark: 'vc-bg-gray-900 vc-border-2 border-pink',
                    },
                    bgAccentLow: {
                        light: 'bg-pink-lightest',
                        dark: 'bg-pink-darkest vc-opacity-75',
                    },
                    bgAccentHigh: {
                        light: 'bg-pink',
                        dark: 'bg-pink',
                    },
                    contentAccent: {
                        light: 'vc-font-bold text-pink',
                        dark: 'vc-font-bold text-pink',
                    },
                    contentAccentContrast: 'vc-font-bold vc-text-white',
                    datePickerInput:
                        '',
                    datePickerInputDrag:
                        '',
                },

                errors: null,
            }
        },

        computed: {
            client: sync('clients/client'),
            creating: sync('clients/creating'),
            users: get('users/users'),

            canCreate() {
                if (!this.$v.$dirty) {
                    return !this.creating
                }

                return !this.$v.$invalid && !this.creating
            },

            currencyOptions() {
                return ['EUR', 'GBP', 'USD']
            },
        },

        async mounted() {
            await this.loadUsers()

            this.client.currency = 'GBP'
        },

        methods: {
            create: call('clients/create'),
            loadUsers: call('users/loadUsers'),
            resetClient: call('clients/resetClient'),
            updateLogo: call('clients/updateLogo'),

            close() {
                this.$v.$reset()
                this.resetClient()

                this.imageSrc = null
                this.imageCroppedSrc = null
                this.creating = false
                this.client.currency = 'GBP'
            },

            async onCreate() {
                if (! this.$v.$invalid) {
                    let client = {
                        currency: this.client.currency,
                        data_started_at: this.client.data_started_at,
                        name: this.client.name,
                        // user_ids: this.client.users.map(user => user.id),
                    }

                    try {
                        client = await this.create(client)

                        if (this.imageCroppedSrc) {
                            await this.uploadImage(client)
                        }

                        this.close()
                        this.$emit('create')
                    } catch (error) {
                        this.errors = error.response.data.errors

                        this.$v.$reset()
                    }
                } else {
                    this.$v.$touch();
                }
            },

            async updateImage(formData) {
                return await this.updateLogo(formData)
            },

            userLabel({full_name, default_client}) {
                return `${full_name} (${default_client.name})`
            },
        },

        validations: {
            client: {
                currency: {
                    required,
                },

                data_started_at: {
                    required,
                },

                name: {
                    required,
                    minLength: minLength(3),
                    maxLength: maxLength(255)
                },
            }
        },

        beforeDestroy() {
            this.close()
        },
    }
</script>
